/**
 * Liest die Meldung aus einem gefangenen Fehler.
 *
 * Ein mit `any` typisierter Catch-Parameter ist der bequeme, aber falsche Weg: er schaltet die
 * Typprüfung für den ganzen Zweig ab. Gefangen wird in JavaScript **jeder** Wert, nicht nur `Error`
 * — deshalb ist `unknown` der richtige Typ und dieser Helfer der eine Ort, an dem geprüft wird, was
 * tatsächlich vorliegt.
 *
 * `bodyText` berücksichtigt den Fall, dass die API einen Klartext statt einer Meldung liefert
 * (siehe `ApiError`).
 */
export function errorMessage(error: unknown, fallback = ""): string {
  if (typeof error === "string") return error || fallback;
  if (error && typeof error === "object") {
    const candidate = error as { bodyText?: unknown; message?: unknown };
    if (typeof candidate.bodyText === "string" && candidate.bodyText.trim()) return candidate.bodyText;
    if (typeof candidate.message === "string" && candidate.message.trim()) return candidate.message;
  }
  return fallback;
}

/**
 * Erkennt den Abbruch einer antd-Formularprüfung.
 *
 * `Form.validateFields()` wirft bei ungültigen Feldern ein Objekt mit `errorFields` — das ist kein
 * Systemfehler, sondern die normale Rückmeldung ans Formular und darf keine Fehlermeldung auslösen.
 */
export function isFormValidationError(error: unknown): boolean {
  return !!error && typeof error === "object" && Array.isArray((error as { errorFields?: unknown }).errorFields);
}

/**
 * Erkennt den Abbruch einer laufenden Anfrage (`AbortController`).
 *
 * Ein Abbruch ist kein Fehler, sondern die Folge einer Nutzeraktion oder eines Wechsels der
 * Ansicht — er darf weder gemeldet noch als Fehlzustand behandelt werden.
 */
const ABORT_ERROR_NAME = "AbortError";

export function isAbortError(error: unknown): boolean {
  if (error instanceof DOMException) return error.name === ABORT_ERROR_NAME;
  return !!error && typeof error === "object" && (error as { name?: unknown }).name === ABORT_ERROR_NAME;
}

/** Liest den `name` eines gefangenen Fehlers (z. B. Browser-Fehlerklassen wie `NotAllowedError`). */
export function errorName(error: unknown): string {
  if (error instanceof Error) return error.name;
  if (error && typeof error === "object") {
    const value = (error as { name?: unknown }).name;
    if (typeof value === "string") return value;
  }
  return "";
}

/** Liest den HTTP-Status eines gefangenen Fehlers, sofern es einer ist. */
export function httpStatusOf(error: unknown): number | null {
  if (!error || typeof error !== "object") return null;
  const value = (error as { status?: unknown }).status;
  return typeof value === "number" ? value : null;
}

/** Liest den rohen Antworttext eines gefangenen API-Fehlers (leer, wenn keiner vorliegt). */
export function errorBodyText(error: unknown): string {
  if (!error || typeof error !== "object") return "";
  const value = (error as { bodyText?: unknown }).bodyText;
  return typeof value === "string" ? value.trim() : "";
}

/** Liest die Fehlerliste aus einer API-Antwort (`{ errors: [...] }`), sonst eine leere Liste. */
export function apiErrorList(error: unknown): string[] {
  const raw = errorBodyText(error);
  if (raw) {
    try {
      const parsed = JSON.parse(raw) as { errors?: unknown };
      if (Array.isArray(parsed?.errors)) return parsed.errors.map((x) => String(x));
    } catch {
      // Kein JSON — dann gibt es auch keine Fehlerliste.
    }
  }
  if (error && typeof error === "object") {
    const direct = (error as { errors?: unknown }).errors;
    if (Array.isArray(direct)) return direct.map((x) => String(x));
  }
  return [];
}
